import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Share2, PenTool, Plus, Trash2, X, Search, FolderInput, RotateCcw, Copy } from 'lucide-react';
import { documentService } from '../../services/documentService';
import discoveryService from '../../services/discoveryService';
import caseService from '../../services/caseService';
import caseLabel from '../../utils/caseLabel';
import { sendDocumentForSignature, sendSignatureRequest } from '../../services/esignatureService';
import { formatDate } from '../../utils/dateUtils';
import ShareDocumentModal from '../documents/ShareDocumentModal';
import ContactPicker from '../common/ContactPicker';

// dropSignal ({ files, id }) is forwarded from CaseDocumentsTab's full-tab drop
// zone; a new id runs the dropped files through the SAME batch-upload path as the
// Upload button. Drag/drop chrome lives in the wrapper so the whole Documents tab
// is one drop target — this component just consumes the files.
const CaseDocuments = ({ caseId, dropSignal }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showExhibitModal, setShowExhibitModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [exhibitForm, setExhibitForm] = useState({
    exhibit_number: '',
    exhibit_description: '',
  });
  const [savingExhibit, setSavingExhibit] = useState(false);
  const [shareDoc, setShareDoc] = useState(null);

  // Move / delete / restore (doc-mgmt)
  const [moveDoc, setMoveDoc] = useState(null);
  const [moveCases, setMoveCases] = useState([]);
  const [moveSearch, setMoveSearch] = useState('');
  const [moveBusy, setMoveBusy] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicateSets, setDuplicateSets] = useState([]);
  const lastFilesRef = useRef({});
  const [deletedDocs, setDeletedDocs] = useState([]);

  // Multi-file upload
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [lastDropId, setLastDropId] = useState(null);

  // Signature modal state
  const [signatureDoc, setSignatureDoc] = useState(null);
  const [signatureSigners, setSignatureSigners] = useState([{ name: '', email: '', type: 'client' }]);
  const [sendingForSignature, setSendingForSignature] = useState(false);
  const [signaturePosition, setSignaturePosition] = useState({ top: 0, left: 0 });

  const categories = [
    'documents',
    'correspondence',
    'pleadings',
    'discovery',
    'evidence',
  ];

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    loadDocuments();
  }, [caseId, selectedCategory, debouncedSearch]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await documentService.getCaseDocuments(caseId, selectedCategory || null, debouncedSearch || null);
      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFiles = async (fileList, override = false) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    // stash the File objects so a soft-blocked duplicate can be re-sent with override
    files.forEach((f) => { lastFilesRef.current[f.name] = f; });
    try {
      setUploading(true);
      if (!override) setUploadResults(null);
      setUploadProgress(0);
      const data = await documentService.uploadDocumentsBatch(files, 'case', {
        caseId: caseId,
        category: selectedCategory || 'documents',
        override,
      }, (pct) => setUploadProgress(pct));
      // Per-file outcomes stay on screen — one bad file never hides the others.
      // On an override re-send, replace just those filenames in the existing panel.
      setUploadResults((prev) => {
        const incoming = data.results || [];
        if (!override || !prev) return incoming;
        const byName = new Map(incoming.map((r) => [r.filename, r]));
        return prev.map((r) => byName.get(r.filename) || r);
      });
      loadDocuments();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Upload failed';
      setUploadResults(files.map((f) => ({ filename: f.name, success: false, error: msg })));
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleUploadAnyway = (filename) => {
    const f = lastFilesRef.current[filename];
    if (f) handleFiles([f], true);
  };

  const handleUpload = async (e) => {
    await handleFiles(e.target.files);
    e.target.value = '';
  };

  // Consume files dropped on the wrapper's full-tab drop zone (single upload path).
  useEffect(() => {
    if (dropSignal && dropSignal.id && dropSignal.id !== lastDropId && dropSignal.files?.length) {
      setLastDropId(dropSignal.id);
      handleFiles(dropSignal.files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropSignal]);

  const handleDelete = async (doc) => {
    const reason = window.prompt(
      `Delete "${(doc.display_name || doc.original_file_name)}"?\nThis is a soft delete (admin-recoverable). Enter a reason:`);
    if (reason === null) return;
    if (!reason.trim()) { alert('A deletion reason is required'); return; }
    try {
      await documentService.deleteDocument(doc.id, reason.trim());
      loadDocuments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete document');
    }
  };

  const openMoveModal = async (doc) => {
    setMoveDoc(doc);
    setMoveSearch('');
    try {
      const data = await caseService.getCases({ limit: 500 });
      const cases = Array.isArray(data) ? data : (data.cases || []);
      setMoveCases(cases.filter((c) => c.id !== caseId));
    } catch (err) {
      setMoveCases([]);
    }
  };

  const handleMove = async (targetCaseId) => {
    if (!moveDoc) return;
    try {
      setMoveBusy(true);
      const res = await documentService.moveDocument(moveDoc.id, targetCaseId);
      const carried = res.carried_flags || {};
      if (carried.is_discoverable || carried.is_exhibit) {
        alert(`${res.message}\nNote: discovery/exhibit flags carried with the document.`);
      }
      setMoveDoc(null);
      loadDocuments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to move document');
    } finally {
      setMoveBusy(false);
    }
  };

  const loadDeleted = async () => {
    try {
      const data = await documentService.getDeletedCaseDocuments(caseId);
      setDeletedDocs(data.documents || []);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to load deleted documents');
      setShowDeleted(false);
    }
  };

  const toggleDeletedView = async () => {
    const next = !showDeleted;
    setShowDeleted(next);
    setShowDuplicates(false);
    if (next) await loadDeleted();
  };

  const loadDuplicates = async () => {
    try {
      const data = await documentService.getCaseDuplicates(caseId);
      setDuplicateSets(data.duplicate_sets || []);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to load duplicates');
      setShowDuplicates(false);
    }
  };

  const toggleDuplicatesView = async () => {
    const next = !showDuplicates;
    setShowDuplicates(next);
    setShowDeleted(false);
    if (next) await loadDuplicates();
  };

  const handleRestore = async (docId) => {
    try {
      await documentService.restoreDocument(docId);
      await loadDeleted();
      loadDocuments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to restore document');
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      await documentService.downloadDocument(docId, fileName);
    } catch (err) {
      alert('Failed to download document');
    }
  };

  const handleToggleDiscoverable = async (docId, isDiscoverable) => {
    try {
      await discoveryService.updateDocumentDiscoverable(docId, { is_discoverable: isDiscoverable });
      loadDocuments();
    } catch (err) {
      alert('Failed to update document');
    }
  };

  const openExhibitModal = (doc) => {
    setSelectedDoc(doc);
    setExhibitForm({
      exhibit_number: doc.exhibit_number || '',
      exhibit_description: doc.exhibit_description || '',
    });
    setShowExhibitModal(true);
  };

  const handleSaveExhibit = async () => {
    if (!selectedDoc) return;
    try {
      setSavingExhibit(true);
      await discoveryService.updateDocumentExhibit(selectedDoc.id, {
        is_exhibit: true,
        exhibit_number: exhibitForm.exhibit_number || null,
        exhibit_description: exhibitForm.exhibit_description || null,
      });
      setShowExhibitModal(false);
      setSelectedDoc(null);
      loadDocuments();
    } catch (err) {
      alert('Failed to save exhibit');
    } finally {
      setSavingExhibit(false);
    }
  };

  const handleRemoveExhibit = async (docId) => {
    try {
      await discoveryService.updateDocumentExhibit(docId, {
        is_exhibit: false,
      });
      loadDocuments();
    } catch (err) {
      alert('Failed to remove exhibit');
    }
  };

  // Signature handlers
  const handleSendForSignature = (e, doc) => {
    const rect = e.currentTarget.getBoundingClientRect();

    // Position the popover below the button using fixed positioning (viewport coords)
    let top = rect.bottom + 8;
    let left = rect.left;

    // Make sure popover doesn't go off-screen to the right
    const popoverWidth = 480;
    if (left + popoverWidth > window.innerWidth - 20) {
      left = window.innerWidth - popoverWidth - 20;
    }

    // Make sure popover doesn't go off-screen at the bottom
    const popoverHeight = 300;
    if (top + popoverHeight > window.innerHeight - 20) {
      // Position above the button instead
      top = rect.top - popoverHeight - 8;
    }

    setSignaturePosition({ top, left });
    setSignatureDoc(doc);
    setSignatureSigners([{ name: '', email: '', type: 'client' }]);
  };

  const handleAddSignatureSigner = () => {
    setSignatureSigners([...signatureSigners, { name: '', email: '', type: 'client' }]);
  };

  const handleRemoveSignatureSigner = (index) => {
    if (signatureSigners.length > 1) {
      setSignatureSigners(signatureSigners.filter((_, i) => i !== index));
    }
  };

  const handleSignatureSignerChange = (index, field, value) => {
    const updated = [...signatureSigners];
    updated[index][field] = value;
    setSignatureSigners(updated);
  };

  const handleSubmitForSignature = async () => {
    const validSigners = signatureSigners.filter(s => s.name.trim() && s.email.trim());
    if (validSigners.length === 0) {
      alert('Please add at least one signer with name and email');
      return;
    }

    try {
      setSendingForSignature(true);
      // Step 1: Create the signature request
      const response = await sendDocumentForSignature({
        document_id: signatureDoc.id,
        document_type: 'virgo_document',
        signers: validSigners.map((s, idx) => ({
          signer_name: s.name,
          signer_email: s.email,
          signer_type: s.type,
          signing_order: idx + 1
        })),
        case_id: caseId
      });

      // Step 2: Send the signature request (emails signers)
      const requestId = response.data.id;
      await sendSignatureRequest(requestId);

      alert('Document sent for signature! The signer(s) will receive an email shortly.');
      setSignatureDoc(null);
    } catch (err) {
      console.error('Failed to send for signature:', err);
      alert(err.response?.data?.detail || 'Failed to send document for signature');
    } finally {
      setSendingForSignature(false);
    }
  };

  const formatFileSize = (bytes) => {
    return documentService.formatFileSize(bytes);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6 relative">
      {/* Move-to-case Modal */}
      {moveDoc && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-1">Move to case…</h3>
            <p className="text-sm text-virgo-teal-400 mb-4 truncate">{(moveDoc.display_name || moveDoc.original_file_name)}</p>
            <input
              type="text"
              value={moveSearch}
              onChange={(e) => setMoveSearch(e.target.value)}
              placeholder="Search cases..."
              className="w-full px-3 py-2 mb-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
              autoFocus
            />
            <div className="max-h-64 overflow-y-auto space-y-1 mb-4">
              {moveCases
                .filter((c) => caseLabel(c).toLowerCase().includes(moveSearch.toLowerCase()))
                .slice(0, 25)
                .map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleMove(c.id)}
                    disabled={moveBusy}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-virgo-navy-600 text-sm text-white disabled:opacity-50 transition-colors"
                  >
                    {caseLabel(c)}
                  </button>
                ))}
              {moveCases.length === 0 && (
                <p className="text-sm text-virgo-teal-500 text-center py-4">No other cases found</p>
              )}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setMoveDoc(null)}
                className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exhibit Modal */}
      {showExhibitModal && selectedDoc && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-1">Mark as Exhibit</h3>
            <p className="text-sm text-virgo-teal-400 mb-4 truncate">{(selectedDoc.display_name || selectedDoc.original_file_name)}</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                  Exhibit Number
                </label>
                <input
                  type="text"
                  value={exhibitForm.exhibit_number}
                  onChange={(e) => setExhibitForm({ ...exhibitForm, exhibit_number: e.target.value })}
                  placeholder="e.g., A, 1, Plaintiff-1"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={exhibitForm.exhibit_description}
                  onChange={(e) => setExhibitForm({ ...exhibitForm, exhibit_description: e.target.value })}
                  placeholder="Brief description of the exhibit..."
                  rows={3}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => { setShowExhibitModal(false); setSelectedDoc(null); }}
                className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveExhibit}
                disabled={savingExhibit}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all"
              >
                {savingExhibit ? 'Saving...' : 'Save Exhibit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload and Filter */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-virgo-teal-500" size={18} />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 w-64"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleDuplicatesView}
            className={`px-3 py-2 text-sm rounded-lg font-medium border transition-colors flex items-center gap-1 ${
              showDuplicates
                ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                : 'bg-virgo-navy-600 text-virgo-teal-400 border-virgo-teal-700/50 hover:bg-virgo-navy-500'
            }`}
            title="Same-case duplicate documents"
          >
            <Copy size={14} /> {showDuplicates ? 'Back to documents' : 'Duplicates'}
          </button>
          <button
            onClick={toggleDeletedView}
            className={`px-3 py-2 text-sm rounded-lg font-medium border transition-colors ${
              showDeleted
                ? 'bg-red-500/20 text-red-300 border-red-500/30'
                : 'bg-virgo-navy-600 text-virgo-teal-400 border-virgo-teal-700/50 hover:bg-virgo-navy-500'
            }`}
            title="Admin: view/restore deleted documents"
          >
            {showDeleted ? 'Back to documents' : 'Deleted'}
          </button>
          <input
            type="file"
            id="file-upload"
            onChange={handleUpload}
            className="hidden"
            disabled={uploading}
            multiple
          />
          <label
            htmlFor="file-upload"
            className={`px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 font-medium cursor-pointer shadow-lg shadow-cyan-500/30 transition-all ${
              uploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {uploading
              ? `Uploading…${uploadProgress != null ? ` ${uploadProgress}%` : ''}`
              : '+ Upload Documents'}
          </label>
        </div>
      </div>

      {/* Per-file upload results — failures stay visible, never silent */}
      {uploadResults && (
        <div className="mb-6 bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-white">
              Upload results: {uploadResults.filter(r => r.success).length} of {uploadResults.length} succeeded
            </p>
            <button onClick={() => setUploadResults(null)}
                    className="text-virgo-teal-400 hover:text-white"><X size={16} /></button>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {uploadResults.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span>{r.blocked ? '⚠️' : r.success ? '✅' : r.within_batch ? '⏭️' : '❌'}</span>
                <span className="text-virgo-teal-200 truncate">{r.filename}</span>
                {r.success && r.annotation && (
                  <span className="text-amber-300/90 text-xs truncate">— {r.annotation}</span>
                )}
                {!r.success && (
                  <span className={`text-xs truncate ${r.blocked ? 'text-amber-400' : r.within_batch ? 'text-virgo-teal-500' : 'text-red-400'}`}>— {r.error}</span>
                )}
                {r.blocked && r.can_override && (
                  <button
                    onClick={() => handleUploadAnyway(r.filename)}
                    disabled={uploading}
                    className="ml-auto shrink-0 px-2 py-0.5 text-xs bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded hover:bg-amber-500/30 disabled:opacity-50"
                  >
                    Upload anyway
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deleted-documents view (admin restore worklist) */}
      {showDeleted ? (
        deletedDocs.length === 0 ? (
          <div className="text-center py-12 text-virgo-teal-400">No deleted documents on this case.</div>
        ) : (
          <div className="space-y-2">
            {deletedDocs.map((d) => (
              <div key={d.id} className="flex items-center justify-between p-4 bg-virgo-navy-800/50 border border-red-500/20 rounded-xl">
                <div className="min-w-0">
                  <p className="font-medium text-white truncate">{d.file_name}</p>
                  <p className="text-xs text-virgo-teal-400">
                    Deleted {d.deleted_at ? formatDate(d.deleted_at) : '-'} by {d.deleted_by} — “{d.delete_reason}”
                  </p>
                </div>
                <button
                  onClick={() => handleRestore(d.id)}
                  className="px-3 py-2 text-sm bg-green-500/20 text-green-300 border border-green-500/30 rounded-lg hover:bg-green-500/30 font-medium transition-colors flex items-center gap-1"
                >
                  <RotateCcw size={14} /> Restore
                </button>
              </div>
            ))}
          </div>
        )
      ) : null}

      {/* Duplicates view — same-case duplicate sets; move/delete apply the produced-doc guard */}
      {showDuplicates ? (
        duplicateSets.length === 0 ? (
          <div className="text-center py-12 text-virgo-teal-400">No duplicate documents on this case.</div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-virgo-teal-400">
              {duplicateSets.length} duplicate set{duplicateSets.length !== 1 ? 's' : ''} — identical file content within this case.
            </p>
            {duplicateSets.map((set) => (
              <div key={set.file_hash} className="bg-virgo-navy-800/50 border border-amber-500/20 rounded-xl p-3">
                <p className="text-xs text-virgo-teal-500 mb-2 font-mono">sha256 {set.file_hash.slice(0, 16)}… · {set.count} copies</p>
                <div className="space-y-2">
                  {set.documents.map((d, idx) => (
                    <div key={d.id} className="flex items-center justify-between p-2 bg-virgo-navy-800/60 rounded-lg">
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">
                          {idx === 0 && <span className="text-green-400 text-xs mr-1">[original]</span>}
                          {d.file_name}
                        </p>
                        <p className="text-xs text-virgo-teal-500">
                          {formatFileSize(d.file_size)} · {d.uploaded_at ? formatDate(d.uploaded_at) : '-'} · {d.uploaded_by}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => openMoveModal({ id: d.id, original_file_name: d.file_name })}
                          className="px-2 py-1 text-xs bg-virgo-navy-600 text-virgo-teal-400 border border-virgo-teal-700/50 rounded hover:bg-virgo-navy-500 flex items-center gap-1"
                          title="Move to another case"
                        >
                          <FolderInput size={12} />
                        </button>
                        <button
                          onClick={async () => { await handleDelete({ id: d.id, original_file_name: d.file_name }); loadDuplicates(); }}
                          className="px-2 py-1 text-xs bg-red-500/10 text-red-400 border border-red-500/30 rounded hover:bg-red-500/20 flex items-center gap-1"
                          title="Delete (produced/e-signed documents are refused)"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}

      {/* Documents Grid */}
      {!showDeleted && !showDuplicates && (documents.length === 0 ? (
        <div className="text-center py-12 text-virgo-teal-400">
          No documents in this category. Upload your first document above.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className={`bg-virgo-navy-800/50 border rounded-xl p-4 transition-all ${
                doc.is_exhibit
                  ? 'border-amber-500/50 hover:border-amber-400/70'
                  : 'border-virgo-teal-700/30 hover:border-cyan-500/30'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="text-4xl">
                    {doc.mime_type.includes('pdf') ? '📕' :
                     doc.mime_type.includes('image') ? '🖼️' :
                     doc.mime_type.includes('word') ? '📘' :
                     doc.mime_type.includes('excel') ? '📗' :
                     '📄'}
                  </div>
                  {doc.is_exhibit && doc.exhibit_number && (
                    <div className="px-2 py-1 bg-amber-500/30 border border-amber-500/50 rounded text-amber-300 text-sm font-bold">
                      Ex. {doc.exhibit_number}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 items-end">
                  {doc.is_exhibit && (
                    <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded">
                      Exhibit
                    </span>
                  )}
                  {doc.is_discoverable && (
                    <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-300 border border-green-500/30 rounded">
                      Discovery
                    </span>
                  )}
                  {doc.is_confidential && (
                    <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-300 border border-red-500/30 rounded">
                      Confidential
                    </span>
                  )}
                </div>
              </div>

              <h4 className="font-semibold text-white mb-1 truncate" title={(doc.display_name || doc.original_file_name)}>
                {(doc.display_name || doc.original_file_name)}
              </h4>

              {doc.is_exhibit && doc.exhibit_description && (
                <p className="text-xs text-amber-300/80 mb-2 line-clamp-2">{doc.exhibit_description}</p>
              )}

              <div className="text-sm text-virgo-teal-400 space-y-1 mb-3">
                <p>Category: {doc.category}</p>
                <p>Size: {formatFileSize(doc.file_size)}</p>
                <p>Uploaded: {formatDate(doc.uploaded_at)}</p>
                <p className="text-xs text-virgo-teal-500">By: {doc.uploaded_by}</p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleDownload(doc.id, (doc.display_name || doc.original_file_name))}
                  className="flex-1 px-3 py-2 text-sm bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded-lg hover:bg-blue-500/30 font-medium transition-colors"
                >
                  Download
                </button>
                <button
                  onClick={() => setShareDoc(doc)}
                  className="px-3 py-2 text-sm bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/30 font-medium transition-colors flex items-center gap-1"
                  title="Share with client"
                >
                  <Share2 size={14} />
                  Share
                </button>
                {doc.mime_type === 'application/pdf' && (
                  <button
                    onClick={(e) => handleSendForSignature(e, doc)}
                    className="px-3 py-2 text-sm bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-lg hover:bg-purple-500/30 font-medium transition-colors flex items-center gap-1"
                    title="Send for signature"
                  >
                    <PenTool size={14} />
                    Sign
                  </button>
                )}
                <button
                  onClick={() => doc.is_exhibit ? handleRemoveExhibit(doc.id) : openExhibitModal(doc)}
                  className={`px-3 py-2 text-sm rounded-lg font-medium transition-colors ${
                    doc.is_exhibit
                      ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30'
                      : 'bg-virgo-navy-600 text-virgo-teal-400 border border-virgo-teal-700/50 hover:bg-virgo-navy-500'
                  }`}
                  title={doc.is_exhibit ? 'Remove exhibit' : 'Mark as exhibit'}
                >
                  {doc.is_exhibit ? `Ex ${doc.exhibit_number || ''}` : '+ Exhibit'}
                </button>
                <button
                  onClick={() => handleToggleDiscoverable(doc.id, !doc.is_discoverable)}
                  className={`px-3 py-2 text-sm rounded-lg font-medium transition-colors ${
                    doc.is_discoverable
                      ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                      : 'bg-virgo-navy-600 text-virgo-teal-400 border border-virgo-teal-700/50 hover:bg-virgo-navy-500'
                  }`}
                  title={doc.is_discoverable ? 'Remove from discovery' : 'Mark for discovery'}
                >
                  {doc.is_discoverable ? '✓' : '+'}
                </button>
                <button
                  onClick={() => openMoveModal(doc)}
                  className="px-3 py-2 text-sm bg-virgo-navy-600 text-virgo-teal-400 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 font-medium transition-colors flex items-center gap-1"
                  title="Move to another case (refused for produced/e-signed documents)"
                >
                  <FolderInput size={14} />
                </button>
                <button
                  onClick={() => handleDelete(doc)}
                  className="px-3 py-2 text-sm bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/20 font-medium transition-colors flex items-center gap-1"
                  title="Delete (soft; reason required; refused for produced/e-signed documents)"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Share Document Modal */}
      <ShareDocumentModal
        isOpen={!!shareDoc}
        onClose={() => setShareDoc(null)}
        documents={shareDoc ? [{
          id: shareDoc.id,
          name: (shareDoc.display_name || shareDoc.original_file_name),
          protections: shareDoc.protections || []
        }] : []}
        caseId={caseId}
        onShare={() => {
          setShareDoc(null);
          loadDocuments();
        }}
      />

      {/* Send for Signature Popover */}
      {signatureDoc && (
        <>
          {/* Backdrop to close on click outside */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setSignatureDoc(null)}
          />
          {/* Popover positioned near the clicked button */}
          <div
            className="fixed z-50 w-[480px] bg-virgo-navy-700 rounded-xl border border-purple-500/30 shadow-2xl shadow-purple-500/20"
            style={{
              top: signaturePosition.top,
              left: signaturePosition.left,
            }}
          >
            {/* Arrow pointer */}
            <div className="absolute -top-2 left-4 w-4 h-4 bg-virgo-navy-700 border-l border-t border-purple-500/30 transform rotate-45" />

            <div className="px-4 py-3 border-b border-virgo-navy-600 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PenTool size={16} className="text-purple-400" />
                <span className="text-sm font-semibold text-white">Send for Signature</span>
              </div>
              <button
                onClick={() => setSignatureDoc(null)}
                className="text-virgo-teal-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <p className="text-xs text-virgo-teal-400 truncate">{(signatureDoc.display_name || signatureDoc.original_file_name)}</p>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-virgo-teal-300">
                    Signers
                  </label>
                  <button
                    type="button"
                    onClick={handleAddSignatureSigner}
                    className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    <Plus size={12} />
                    Add
                  </button>
                </div>
                <div className="mb-2">
                  <ContactPicker
                    placeholder="Add signer from contacts…"
                    allowRawEmail
                    excludeEmails={signatureSigners.map(s => s.email).filter(Boolean)}
                    onSelect={(person) => setSignatureSigners(prev => {
                      const row = { name: person.name || '', email: person.email || '', type: 'client' };
                      const emptyIdx = prev.findIndex(s => !s.name && !s.email);
                      if (emptyIdx >= 0) return prev.map((s, i) => (i === emptyIdx ? row : s));
                      return [...prev, row];
                    })}
                  />
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {signatureSigners.map((signer, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={signer.name}
                        onChange={(e) => handleSignatureSignerChange(index, 'name', e.target.value)}
                        placeholder="Name"
                        className="flex-1 px-2 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white placeholder-virgo-teal-500 text-xs focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <input
                        type="email"
                        value={signer.email}
                        onChange={(e) => handleSignatureSignerChange(index, 'email', e.target.value)}
                        placeholder="Email"
                        className="flex-1 px-2 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white placeholder-virgo-teal-500 text-xs focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <select
                        value={signer.type}
                        onChange={(e) => handleSignatureSignerChange(index, 'type', e.target.value)}
                        className="w-24 px-1 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white text-xs focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="client">Client</option>
                        <option value="attorney">Attorney</option>
                        <option value="witness">Witness</option>
                        <option value="other">Other</option>
                      </select>
                      {signatureSigners.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSignatureSigner(index)}
                          className="p-1 text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-virgo-navy-600 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSignatureDoc(null)}
                className="px-3 py-1.5 text-xs font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitForSignature}
                disabled={sendingForSignature}
                className="px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {sendingForSignature ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <PenTool size={14} />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CaseDocuments;
